/**
 * The four tabs of the address book, in the order the API enumerates them.
 *
 * They live next to the routes rather than in the page because three of the four
 * things a tab is are routing concerns: the named route it navigates to, the key
 * the last visited one is remembered under, and the `tab` value the listing is
 * requested with. Keeping the label here too gives the shell one array to render
 * from, so a tab cannot exist in the bar without a route behind it, nor a route
 * without a label — the two ways this used to drift.
 *
 * `id` duplicates the route name on purpose: `resolveTargetTabId` matches on an
 * `id` key (`@/router/utils/lastTabStorage`), and feeding it this same list is
 * what keeps the remembered tab and the declared routes from disagreeing.
 *
 * The labels reuse keys that already exist in the API's lang files rather than
 * minting an `address_book_tab_*` family: this repository namespaces keys by
 * lang file and has no such convention, and a key added here but absent from
 * Lokalise is overwritten on the next import — it would render as its own name.
 *
 * `icon` is the FontAwesome name `BaseSimpleTab` draws before the label
 * (`BaseSimpleTab.vue:20`), which it already reads off each tab — the shared
 * component needed no change to carry one. Each is the icon the side menu
 * already gives that same section (`@/store/modules/side-menu`): `mailbox` for
 * the messaging, `folder` for the dossiers, `truck` for the suppliers. The tabs
 * are four *sources* of contacts, and naming a source here with a different
 * glyph than the menu names it with would make the two read as different
 * things. Only the internal tab has no menu entry to borrow from, the colleague
 * list living under the settings; `users` is the plain reading of it.
 *
 * All four are registered in the light set of the shared FontAwesome library
 * (`@c/baseComponents/fontawesome`), which is the only set `BaseSimpleTab` asks
 * for — an unregistered name renders nothing at all, silently.
 */
export const ADDRESS_BOOK_TABS = [
  {
    id: 'address_book_messaging_route',
    tab: 'messaging',
    labelKey: 'navigation.messaging_title',
    icon: 'mailbox'
  },
  {
    id: 'address_book_dossiers_route',
    tab: 'dossiers',
    labelKey: 'navigation.dossier_title',
    icon: 'folder'
  },
  {
    id: 'address_book_suppliers_route',
    tab: 'suppliers',
    labelKey: 'navigation.content_supplier_title',
    icon: 'truck'
  },
  {
    id: 'address_book_internal_route',
    tab: 'internal',
    labelKey: 'global.intern',
    icon: 'users'
  }
]

/**
 * Where the last visited tab is remembered, following the shape of the keys the
 * global `router.afterEach` already writes (`dossier:last_subpage_route_name`).
 *
 * @type {string}
 */
export const ADDRESS_BOOK_LAST_TAB_KEY = 'address_book:last_tab_route_name'

/**
 * The route names of the four tabs, for the `afterEach` that records them.
 *
 * @type {string[]}
 */
export const ADDRESS_BOOK_TAB_ROUTE_NAMES = ADDRESS_BOOK_TABS.map(
  (addressBookTab) => addressBookTab.id
)
